'use client';

/**
 * API Contract Designer
 * Design and document APIs with OpenAPI spec generation
 */

import { useState, useEffect, useCallback } from 'react';
import ToolSEO from '@/components/ToolSEO';
import {
  APIContract,
  Endpoint,
  HttpMethod,
  HTTP_METHOD_CONFIG,
} from '@/lib/api-contract/types';
import * as storage from '@/lib/api-contract/storage';
import { exportToJSON, exportToYAML, generateMarkdown } from '@/lib/api-contract/openapi-generator';
import { EndpointList } from '@/components/api-contract/EndpointList';
import { EndpointEditor } from '@/components/api-contract/EndpointEditor';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };

type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    canEdit: tierLevel >= TIER_HIERARCHY.free,
    canSave: tierLevel >= TIER_HIERARCHY.free,
    maxContracts: tier === 'free' ? 3 : Infinity,
    maxEndpoints: tier === 'free' ? 10 : Infinity,
    canImport: tierLevel >= TIER_HIERARCHY.pro,
    canExportYaml: tierLevel >= TIER_HIERARCHY.pro,
    canExportMarkdown: tierLevel >= TIER_HIERARCHY.pro,
    canDetectBreakingChanges: tierLevel >= TIER_HIERARCHY.pro,
    canGenerateMocks: tierLevel >= TIER_HIERARCHY.pro,
    canTrackConsumers: tierLevel >= TIER_HIERARCHY.team,
  };
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function APIContractPage() {
  const [userTier] = useState<UserTier>('pro'); // TODO: Get from auth
  const [contracts, setContracts] = useState<APIContract[]>([]);
  const [selectedContract, setSelectedContract] = useState<APIContract | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialogs
  const [showNewContract, setShowNewContract] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [newContractName, setNewContractName] = useState('');

  const features = getFeatureAccess(userTier);

  // Load contracts
  useEffect(() => {
    async function load() {
      try {
        const list = await storage.listContracts();
        setContracts(list);
        if (list.length > 0) {
          setSelectedContract(list[0]);
        }
      } catch (err) {
        console.error('[APIContract] Failed to load contracts:', err);
        setError('Failed to load contracts');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Auto-save on changes
  const saveContract = useCallback(async (contract: APIContract) => {
    if (!features.canSave) return;

    setSaving(true);
    try {
      await storage.saveContract(contract);
      setContracts((prev) =>
        prev.map((c) => (c.id === contract.id ? contract : c))
      );
    } catch (err) {
      console.error('[APIContract] Failed to save:', err);
      setError('Failed to save contract');
    } finally {
      setSaving(false);
    }
  }, [features.canSave]);

  // Handlers
  const handleCreateContract = async () => {
    if (!newContractName.trim()) return;

    if (contracts.length >= features.maxContracts) {
      setError(`Free tier limited to ${features.maxContracts} contracts. Upgrade to Pro for more.`);
      return;
    }

    try {
      const contract = await storage.createContract(newContractName.trim());
      setContracts((prev) => [contract, ...prev]);
      setSelectedContract(contract);
      setShowNewContract(false);
      setNewContractName('');
    } catch (err) {
      setError('Failed to create contract');
    }
  };

  const handleDeleteContract = async (id: string) => {
    if (!confirm('Delete this API contract? This cannot be undone.')) return;

    try {
      await storage.deleteContract(id);
      setContracts((prev) => prev.filter((c) => c.id !== id));
      if (selectedContract?.id === id) {
        setSelectedContract(contracts.find((c) => c.id !== id) || null);
        setSelectedEndpoint(null);
      }
    } catch (err) {
      setError('Failed to delete contract');
    }
  };

  const handleAddEndpoint = () => {
    if (!selectedContract) return;

    if (selectedContract.endpoints.length >= features.maxEndpoints) {
      setError(`Free tier limited to ${features.maxEndpoints} endpoints. Upgrade to Pro for more.`);
      return;
    }

    const newEndpoint: Endpoint = {
      id: generateId(),
      path: '/new-endpoint',
      method: 'GET',
      parameters: [],
      responses: [
        {
          id: generateId(),
          statusCode: '200',
          description: 'Successful response',
        },
      ],
    };

    const updated = {
      ...selectedContract,
      endpoints: [...selectedContract.endpoints, newEndpoint],
    };

    setSelectedContract(updated);
    setSelectedEndpoint(newEndpoint);
    saveContract(updated);
  };

  const handleDeleteEndpoint = (id: string) => {
    if (!selectedContract) return;

    const updated = {
      ...selectedContract,
      endpoints: selectedContract.endpoints.filter((e) => e.id !== id),
    };

    setSelectedContract(updated);
    if (selectedEndpoint?.id === id) {
      setSelectedEndpoint(null);
    }
    saveContract(updated);
  };

  const handleUpdateEndpoint = (endpoint: Endpoint) => {
    if (!selectedContract) return;

    const updated = {
      ...selectedContract,
      endpoints: selectedContract.endpoints.map((e) =>
        e.id === endpoint.id ? endpoint : e
      ),
    };

    setSelectedContract(updated);
    setSelectedEndpoint(endpoint);
    saveContract(updated);
  };

  const handleExport = (format: 'json' | 'yaml' | 'markdown') => {
    if (!selectedContract) return;

    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'json':
        content = exportToJSON(selectedContract);
        filename = `${selectedContract.name.toLowerCase().replace(/\s+/g, '-')}-openapi.json`;
        mimeType = 'application/json';
        break;
      case 'yaml':
        content = exportToYAML(selectedContract);
        filename = `${selectedContract.name.toLowerCase().replace(/\s+/g, '-')}-openapi.yaml`;
        mimeType = 'text/yaml';
        break;
      case 'markdown':
        content = generateMarkdown(selectedContract);
        filename = `${selectedContract.name.toLowerCase().replace(/\s+/g, '-')}-api-docs.md`;
        mimeType = 'text/markdown';
        break;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExport(false);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const contract = await storage.importFromJSON(text);
      setContracts((prev) => [contract, ...prev]);
      setSelectedContract(contract);
    } catch (err) {
      setError('Failed to import contract. Make sure the file is valid JSON.');
    }

    // Reset input
    event.target.value = '';
  };

  return (
    <>
      <ToolSEO
        toolName="API Contract Designer"
        toolSlug="api-contract"
        description="Design and document APIs with visual endpoint builder, OpenAPI spec generation, and breaking change detection."
        category="planner"
        isPremium={true}
      />

      <main className="min-h-screen bg-background text-foreground">
        <div className="flex flex-col h-screen">
          {/* Header */}
          <header className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">API Contract Designer</h1>
                <p className="text-sm text-foreground-tertiary">
                  Design and document APIs with OpenAPI spec generation
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Tier badge */}
                <span className={`
                  px-3 py-1 rounded text-sm font-medium
                  ${userTier === 'team' ? 'bg-purple-500/20 text-purple-400' :
                    userTier === 'pro' ? 'bg-accent-muted text-accent' :
                    'bg-cream/10 text-foreground-tertiary'}
                `}>
                  {userTier.toUpperCase()}
                </span>

                {/* Actions */}
                {selectedContract && (
                  <button
                    onClick={() => setShowExport(true)}
                    className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                  >
                    Export
                  </button>
                )}
                <button
                  onClick={() => setShowNewContract(true)}
                  className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
                >
                  + New Contract
                </button>
              </div>
            </div>

            {/* Contract selector */}
            {contracts.length > 0 && (
              <div className="flex items-center gap-3 mt-4">
                <select
                  value={selectedContract?.id || ''}
                  onChange={(e) => {
                    const contract = contracts.find((c) => c.id === e.target.value);
                    setSelectedContract(contract || null);
                    setSelectedEndpoint(null);
                  }}
                  className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  {contracts.map((contract) => (
                    <option key={contract.id} value={contract.id}>
                      {contract.name} (v{contract.version})
                    </option>
                  ))}
                </select>

                {selectedContract && (
                  <>
                    <input
                      type="text"
                      value={selectedContract.name}
                      onChange={(e) => {
                        const updated = { ...selectedContract, name: e.target.value };
                        setSelectedContract(updated);
                        saveContract(updated);
                      }}
                      className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      placeholder="Contract name"
                    />
                    <input
                      type="text"
                      value={selectedContract.version}
                      onChange={(e) => {
                        const updated = { ...selectedContract, version: e.target.value };
                        setSelectedContract(updated);
                        saveContract(updated);
                      }}
                      className="w-24 px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                      placeholder="1.0.0"
                    />
                    <button
                      onClick={() => handleDeleteContract(selectedContract.id)}
                      className="p-2 text-foreground-tertiary hover:text-red-400 transition-colors"
                      title="Delete contract"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>

                    {saving && (
                      <span className="text-sm text-foreground-tertiary">Saving...</span>
                    )}
                  </>
                )}
              </div>
            )}
          </header>

          {/* Error message */}
          {error && (
            <div className="px-6 py-3 bg-red-500/10 border-b border-red-500/30 text-red-400 flex items-center justify-between">
              {error}
              <button onClick={() => setError(null)} className="underline">Dismiss</button>
            </div>
          )}

          {/* Main content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading contracts...</p>
              </div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-cream/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-foreground-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold mb-2">No API Contracts</h2>
                <p className="text-foreground-tertiary mb-6">Create your first API contract to get started</p>
                <button
                  onClick={() => setShowNewContract(true)}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover"
                >
                  Create API Contract
                </button>
              </div>
            </div>
          ) : selectedContract ? (
            <div className="flex-1 flex overflow-hidden">
              {/* Endpoint list sidebar */}
              <div className="w-80 border-r border-border flex flex-col">
                <EndpointList
                  endpoints={selectedContract.endpoints}
                  selectedId={selectedEndpoint?.id}
                  onSelect={setSelectedEndpoint}
                  onAdd={handleAddEndpoint}
                  onDelete={handleDeleteEndpoint}
                  canEdit={features.canEdit}
                />
              </div>

              {/* Endpoint editor */}
              <div className="flex-1 overflow-hidden">
                {selectedEndpoint ? (
                  <EndpointEditor
                    endpoint={selectedEndpoint}
                    tags={selectedContract.tags?.map((t) => t.name) || []}
                    onChange={handleUpdateEndpoint}
                    canEdit={features.canEdit}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-foreground-tertiary mb-4">Select an endpoint or create a new one</p>
                      <button
                        onClick={handleAddEndpoint}
                        className="px-4 py-2 bg-accent-muted text-accent rounded hover:bg-accent/30"
                      >
                        + Add Endpoint
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Footer */}
          <footer className="px-6 py-3 border-t border-border flex items-center justify-between text-sm text-foreground-tertiary">
            <div>
              {selectedContract && (
                <span>
                  {selectedContract.endpoints.length} endpoint{selectedContract.endpoints.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div>
              OpenAPI 3.0 compatible
            </div>
          </footer>
        </div>

        {/* New Contract Dialog */}
        {showNewContract && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-4">New API Contract</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Name *</label>
                  <input
                    type="text"
                    value={newContractName}
                    onChange={(e) => setNewContractName(e.target.value)}
                    placeholder="e.g., User API"
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowNewContract(false);
                    setNewContractName('');
                  }}
                  className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateContract}
                  disabled={!newContractName.trim()}
                  className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Dialog */}
        {showExport && selectedContract && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-4">Export API Contract</h3>

              <div className="space-y-3">
                <button
                  onClick={() => handleExport('json')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:border-border-muted0 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded flex items-center justify-center">
                      <span className="text-blue-400 font-mono text-sm">{'{}'}</span>
                    </div>
                    <div>
                      <p className="font-medium">OpenAPI JSON</p>
                      <p className="text-sm text-foreground-tertiary">Standard OpenAPI 3.0 format</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => features.canExportYaml ? handleExport('yaml') : null}
                  disabled={!features.canExportYaml}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    features.canExportYaml
                      ? 'border-border hover:border-border-muted0'
                      : 'border-border-muted opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded flex items-center justify-center">
                      <span className="text-green-400 font-mono text-sm">yml</span>
                    </div>
                    <div>
                      <p className="font-medium">OpenAPI YAML</p>
                      <p className="text-sm text-foreground-tertiary">
                        {features.canExportYaml ? 'Human-readable format' : 'Pro feature'}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => features.canExportMarkdown ? handleExport('markdown') : null}
                  disabled={!features.canExportMarkdown}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    features.canExportMarkdown
                      ? 'border-border hover:border-border-muted0'
                      : 'border-border-muted opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded flex items-center justify-center">
                      <span className="text-purple-400 font-mono text-sm">md</span>
                    </div>
                    <div>
                      <p className="font-medium">Markdown Docs</p>
                      <p className="text-sm text-foreground-tertiary">
                        {features.canExportMarkdown ? 'Human-readable documentation' : 'Pro feature'}
                      </p>
                    </div>
                  </div>
                </button>

                {features.canImport && (
                  <div className="pt-4 border-t border-border">
                    <label className="w-full p-4 text-left border border-border rounded-lg hover:border-border-muted0 transition-colors flex items-center gap-3 cursor-pointer">
                      <div className="w-10 h-10 bg-accent-muted rounded flex items-center justify-center">
                        <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium">Import Contract</p>
                        <p className="text-sm text-foreground-tertiary">Import from JSON file</p>
                      </div>
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImport}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowExport(false)}
                  className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
